import extractApiErrors, { errorEntryMessage } from './extractApiErrors';
// The base no-unused-vars rule does not see type-only uses.
// eslint-disable-next-line no-unused-vars
import { hasErrors, RowErrors } from './rowErrors';

/**
 * Turns portal web service errors into the messages a page shows. Used by the base rates and
 * pricing model pages and SpreadsheetUpload; the list helpers place a failed save of a versioned
 * list on its rows.
 */

export interface ApiErrorEntry {
  status?: number;
  errorDescription?: string;
  message?: string;
  name?: string;
  path?: (string | number)[];
}

export interface ApiError {
  status?: number;
  details?: { errors?: ApiErrorEntry[] };
}

const GENERIC_ERROR_MESSAGE = 'Something went wrong';
const INVALID_VALUE_MESSAGE = 'Invalid value';

/** The entry's message, trimmed; '' when the server sent none. */
export const entryMessage = (entry: ApiErrorEntry): string =>
  (errorEntryMessage(entry) || '').trim();

/** Non-blank server messages, in order; callers supply their own fallback when this is empty. */
export const serverMessages = (entries: ApiErrorEntry[]): string[] =>
  entries.map(entryMessage).filter(message => message !== '');

/** The server's messages, or `accessDeniedMessage` for a 403. */
export const describeError = (error: ApiError, accessDeniedMessage: string): string[] => {
  if (error.status === 403) {
    return [accessDeniedMessage];
  }
  const messages = extractApiErrors(error);
  return messages.length > 0 ? messages : [GENERIC_ERROR_MESSAGE];
};

export interface ListErrorOptions<F extends string> {
  // Request property holding the submitted rows, e.g. "basePrices".
  collection: string;
  rowCount: number;
  // Row fields an error path can end in; any other ending applies to the row as a whole.
  fields: readonly F[];
  // Shown for a form-level error the server sent without a message.
  rejectedMessage: string;
}

/**
 * Splits validation errors into those that point at a submitted row (path like
 * [collection, 0, ..., field]) and those that apply to the whole form.
 */
export const splitValidationErrors = <F extends string>(
  entries: ApiErrorEntry[],
  { collection, rowCount, fields, rejectedMessage }: ListErrorOptions<F>
): { rowErrors: RowErrors<F>[]; formErrors: string[] } => {
  const rowErrors: RowErrors<F>[] = Array.from({ length: rowCount }, () => ({}));
  const formErrors: string[] = [];

  entries.forEach(entry => {
    const message = entryMessage(entry);
    const path = entry.path || [];
    const index = path[1];
    if (path[0] !== collection || typeof index !== 'number' || !rowErrors[index]) {
      formErrors.push(message || rejectedMessage);
      return;
    }
    const last = String(path[path.length - 1]);
    const field = fields.find(name => name === last) || 'row';
    rowErrors[index][field] = message || INVALID_VALUE_MESSAGE;
  });

  return { rowErrors, formErrors };
};

export interface SaveFailure<F extends string> {
  // Indexed like the submitted rows.
  rowErrors: RowErrors<F>[];
  formErrors: string[];
  // Server-provided details of a version conflict; null when there is no conflict.
  conflict: string[] | null;
}

/**
 * Describes a failed save of a versioned list: a 409 is a version conflict, a 422 is placed on the
 * rows and fields its paths name, and anything else is a form-level error.
 */
export const describeListSaveError = <F extends string>(
  error: ApiError,
  options: ListErrorOptions<F> & { accessDeniedMessage: string }
): SaveFailure<F> => {
  const entries = (error.details && error.details.errors) || [];
  if (error.status === 409) {
    return { rowErrors: [], formErrors: [], conflict: serverMessages(entries) };
  }
  const { rowErrors, formErrors } = splitValidationErrors(
    error.status === 422 ? entries : [],
    options
  );
  if (formErrors.length === 0 && !hasErrors(rowErrors)) {
    return {
      rowErrors,
      formErrors: describeError(error, options.accessDeniedMessage),
      conflict: null,
    };
  }
  return { rowErrors, formErrors, conflict: null };
};
